import { InPersonComplianceStatusOutContract } from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'

/**
 * Color treatment for the in-person visit card on the visit page. Mirrors the
 * drug screen card so the two compliance rows read as a set.
 * - error   → red surface, used when the annual in-person visit is overdue
 * - warning → yellow surface, used when it is coming due soon
 * - neutral → white surface, used when the patient is compliant
 */
export const IN_PERSON_CARD_VARIANT = {
  error: 'error',
  warning: 'warning',
  neutral: 'neutral',
} as const

export type InPersonCardVariant = (typeof IN_PERSON_CARD_VARIANT)[keyof typeof IN_PERSON_CARD_VARIANT]

/**
 * The detail line under the "In-person visit" title is one of two shapes:
 * - status → a colored warning icon + label (e.g. ⚠ Due now, ⚠ Due soon)
 * - text   → a plain neutral label (e.g. Up to date)
 *
 * The backend only exposes the compliance status, not the last-visit date, so
 * the compliant row shows a status word rather than a date.
 */
type StatusDetail = {
  kind: 'status'
  label: string
}

type TextDetail = {
  kind: 'text'
  label: string
}

export type InPersonComplianceDisplay = {
  cardVariant: InPersonCardVariant
  detail: StatusDetail | TextDetail
}

/**
 * Derives the color-coded compliance display for a patient's annual in-person
 * visit from its status. Returns `null` for `none` (non-Alabama patients, or
 * Alabama patients with no applicable in-person requirement) so the row is
 * hidden entirely — matching how the backend encodes "not applicable".
 */
export function getInPersonComplianceDisplay(
  status?: InPersonComplianceStatusOutContract,
): InPersonComplianceDisplay | null {
  switch (status) {
    case InPersonComplianceStatusOutContract.overdue:
      return {
        cardVariant: IN_PERSON_CARD_VARIANT.error,
        detail: { kind: 'status', label: 'Due now' },
      }
    case InPersonComplianceStatusOutContract.soon:
      return {
        cardVariant: IN_PERSON_CARD_VARIANT.warning,
        detail: { kind: 'status', label: 'Due soon' },
      }
    case InPersonComplianceStatusOutContract.compliant:
      return {
        cardVariant: IN_PERSON_CARD_VARIANT.neutral,
        detail: { kind: 'text', label: 'Up to date' },
      }
    default:
      // `none` and any unrecognized value: the in-person requirement does not
      // apply to this patient, so no row is shown.
      return null
  }
}
